import { Classes } from "@blueprintjs/core";
import classnames from "classnames";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { PlanProject, PlanSection } from "../models/plan-types";
import { PlanAbsenceOption } from "./plan-absences";
import styles from "./ProjectPicker.module.scss";

/** Отступ от края окна, чтобы окно выбора не прилипало к границе */
const VIEWPORT_GAP = 8;

/** На сколько поднять окно над строкой, если оно раскрывается вверх */
const CLICK_OFFSET = 34;

/** Ширины панелей и зазор между ними — те же, что в ProjectPicker.module.scss */
const PROJECTS_WIDTH = 320;
const SECTIONS_WIDTH = 400;
const PANELS_GAP = 6;

/** Подпись стадии для разделов, у которых она не указана */
const NO_STAGE = "Без стадии";

interface ProjectPickerProps {
    x: number;
    y: number;
    projects: PlanProject[];
    /** Отпуск, больничный, БС — те же типы, что в табеле */
    absences: PlanAbsenceOption[];
    /** Сотрудник строки: раздел, где он ответственный, подставляется по умолчанию */
    employerId: string;
    /** Проект, назначенный сейчас (подсвечиваем) */
    currentId?: string | null;
    /** Раздел, назначенный сейчас */
    currentSectionId?: string | null;
    /** Тип отсутствия, стоящий сейчас (подсвечиваем его кнопку) */
    currentAbsence?: string | null;
    /** Разделы состава проекта; null — загрузить не удалось */
    loadSections: (orderId: string) => Promise<PlanSection[] | null>;
    onPick: (orderId: string, sectionId: string | null) => void;
    onPickAbsence: (absence: PlanAbsenceOption) => void;
    onClear: () => void;
    onClose: () => void;
}

/**
 * Выбор проекта и раздела состава проекта. Часы не спрашиваем: плановый день всегда полный.
 *
 * Слева — проекты, справа от них открывается такая же панель с разделами выбранного
 * проекта. Раздел необязателен: сверху панели стоит заметная строка «Без раздела».
 * У проекта без разделов правая панель не открывается — клик сразу ставит проект.
 *
 * Двойной клик по заполненному блоку открывает окно уже на его проекте: проект подсвечен
 * в списке, а справа открыты его разделы с курсором на текущем (или на «Без раздела»).
 * Для нового проекта курсор встаёт на раздел, где сотрудник — единственный ответственный.
 *
 * Тем же окном закрывают день отсутствием: отпуск и больничный в план попадают
 * отсюда, а не только из табеля.
 */
export const ProjectPicker: React.FC<ProjectPickerProps> = (props) => {
    const {
        x,
        y,
        projects,
        absences,
        employerId,
        currentId,
        currentSectionId,
        currentAbsence,
        loadSections,
        onPick,
        onPickAbsence,
        onClear,
        onClose,
    } = props;

    const [query, setQuery] = useState("");
    const [active, setActive] = useState(() =>
        Math.max(
            0,
            projects.findIndex((item) => item.id === currentId)
        )
    );
    const [position, setPosition] = useState({ left: x, top: y, visibility: "hidden" as const });
    /** Правая панель уходит влево от проектов, если справа не хватает места */
    const [sectionsOnLeft, setSectionsOnLeft] = useState(false);

    /**
     * Проект, чьи разделы открыты справа, и сами разделы. Ставятся вместе и только после
     * загрузки: иначе у проекта без разделов панель мигнула бы «загрузкой» и исчезла.
     */
    const [project, setProject] = useState<PlanProject | null>(null);
    const [sections, setSections] = useState<PlanSection[]>([]);
    const [sectionQuery, setSectionQuery] = useState("");
    /** Курсор в правой панели: 0 — «Без раздела», дальше разделы по порядку */
    const [sectionActive, setSectionActive] = useState(0);

    const inputRef = useRef<HTMLInputElement>(null);
    const sectionInputRef = useRef<HTMLInputElement>(null);
    const rootRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const sectionListRef = useRef<HTMLDivElement>(null);
    /** Проект последнего запроса разделов: ответ по уже покинутому проекту выбрасываем */
    const requestRef = useRef<string | null>(null);

    const filtered = useMemo(() => {
        const search = query.trim().toLowerCase();

        if (!search) {
            return projects;
        }

        return projects.filter((item) => `${item.fullInventoryNumber} ${item.name}`.toLowerCase().includes(search));
    }, [projects, query]);

    const filteredSections = useMemo(() => {
        const search = sectionQuery.trim().toLowerCase();

        return sections.filter(
            (section) =>
                !search ||
                `${section.name} ${section.code} ${section.description} ${section.stage}`.toLowerCase().includes(search)
        );
    }, [sections, sectionQuery]);

    const panelOpen = !!project;

    // Окно не должно вылезать за экран. Место считаем по панели проектов: она не прыгает,
    // когда справа открываются разделы, — те при нехватке места встают слева от неё.
    useLayoutEffect(() => {
        const element = rootRef.current;

        if (!element) {
            return;
        }

        const { height } = element.getBoundingClientRect();

        const fitsBelow = y + height + VIEWPORT_GAP <= window.innerHeight;
        const top = fitsBelow ? y : Math.max(VIEWPORT_GAP, y - height - CLICK_OFFSET);

        const projectsLeft = Math.max(VIEWPORT_GAP, Math.min(x, window.innerWidth - PROJECTS_WIDTH - VIEWPORT_GAP));
        const onLeft =
            panelOpen && projectsLeft + PROJECTS_WIDTH + PANELS_GAP + SECTIONS_WIDTH > window.innerWidth - VIEWPORT_GAP;

        setSectionsOnLeft(onLeft);
        setPosition({
            left: onLeft ? Math.max(VIEWPORT_GAP, projectsLeft - SECTIONS_WIDTH - PANELS_GAP) : projectsLeft,
            top,
            visibility: "visible" as any,
        });
    }, [x, y, panelOpen, sections]);

    // подкручиваем списки к курсору: проектов сотни, разделов — десятки
    useEffect(() => {
        scrollToItem(listRef.current, active);
    }, [active]);

    useEffect(() => {
        scrollToItem(sectionListRef.current, sectionActive);
    }, [sectionActive, sections]);

    useEffect(() => {
        const onPointerDown = (event: PointerEvent) => {
            if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
                onClose();
            }
        };

        document.addEventListener("pointerdown", onPointerDown, true);
        return () => document.removeEventListener("pointerdown", onPointerDown, true);
    }, [onClose]);

    /**
     * Открыть разделы проекта справа. Панель показывается только когда разделы уже пришли
     * и они есть. У проекта без разделов панели нет: по клику он ставится сразу, а при
     * открытии окна на блоке просто остаётся подсвеченным в списке.
     */
    const openProject = useCallback(
        async (target: PlanProject, pickIfEmpty: boolean) => {
            requestRef.current = target.id;

            const loaded = await loadSections(target.id);

            if (requestRef.current !== target.id) {
                return;
            }

            requestRef.current = null;

            // удалённый из состава раздел в новых записях не предлагаем
            const list = (loaded || []).filter((section) => !section.inactive);

            if (!list.length) {
                setProject(null);
                setSections([]);

                if (pickIfEmpty) {
                    onPick(target.id, null);
                }

                return;
            }

            setProject(target);
            setSections(list);
            setSectionQuery("");
            setSectionActive(defaultIndex(list, target.id, currentId, currentSectionId, employerId));
        },
        [loadSections, onPick, currentId, currentSectionId, employerId]
    );

    // панель разделов появилась или сменила проект — ввод переходит в её поиск
    useEffect(() => {
        project && sectionInputRef.current && sectionInputRef.current.focus();
    }, [project]);

    // окно открыли на заполненном блоке — сразу показываем разделы его проекта
    useEffect(() => {
        const current = currentId ? projects.find((item) => item.id === currentId) : undefined;

        // фокус сразу на поиске проектов: пока разделы грузятся, можно печатать
        inputRef.current && inputRef.current.focus();

        if (current) {
            openProject(current, false);
        }
        // только при открытии окна
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const pickSection = useCallback(
        (section: PlanSection | null) => {
            project && onPick(project.id, section ? section.id : null);
        },
        [project, onPick]
    );

    const onKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (event.key === "Escape") {
                event.preventDefault();
                onClose();
                return;
            }

            if (event.key === "ArrowDown") {
                event.preventDefault();
                setActive((index) => Math.min(index + 1, filtered.length - 1));
                return;
            }

            if (event.key === "ArrowUp") {
                event.preventDefault();
                setActive((index) => Math.max(index - 1, 0));
                return;
            }

            // Enter или стрелка вправо — к разделам проекта (или сразу поставить, если их нет)
            if (event.key === "Enter" || (event.key === "ArrowRight" && !query)) {
                event.preventDefault();
                const target = filtered[active];
                target && openProject(target, true);
            }
        },
        [filtered, active, query, openProject, onClose]
    );

    const onSectionKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (event.key === "Escape") {
                event.preventDefault();
                onClose();
                return;
            }

            // назад к проектам: стрелка влево или стирание пустого поиска
            if ((event.key === "ArrowLeft" || event.key === "Backspace") && !sectionQuery) {
                event.preventDefault();
                inputRef.current && inputRef.current.focus();
                return;
            }

            if (event.key === "ArrowDown") {
                event.preventDefault();
                setSectionActive((index) => Math.min(index + 1, filteredSections.length));
                return;
            }

            if (event.key === "ArrowUp") {
                event.preventDefault();
                setSectionActive((index) => Math.max(index - 1, 0));
                return;
            }

            if (event.key === "Enter") {
                event.preventDefault();
                pickSection(sectionActive === 0 ? null : filteredSections[sectionActive - 1] || null);
            }
        },
        [sectionQuery, filteredSections, sectionActive, pickSection, onClose]
    );

    const sectionsPanel = project && (
        <div className={classnames(styles.panel, styles.sectionsPanel)}>
            <div className={styles.header}>
                <span className={styles.color} style={{ backgroundColor: project.color }} />
                <span className={styles.number}>{project.fullInventoryNumber}</span>
                <span className={styles.itemName} data-tip={project.fullInventoryNumber} data-tip-note={project.name}>
                    {project.name}
                </span>
            </div>

            <input
                ref={sectionInputRef}
                className={classnames(Classes.INPUT, "planr-default-input", styles.search)}
                placeholder={`Раздел (${sections.length})`}
                autoComplete="off"
                data-lpignore="true"
                value={sectionQuery}
                onChange={(event) => {
                    setSectionQuery(event.target.value);
                    setSectionActive(0);
                }}
                onKeyDown={onSectionKeyDown}
            />

            {/* «Без раздела» — отдельной заметной кнопкой над списком, а не одной строкой из многих */}
            <div className={styles.noSectionWrap} onMouseEnter={() => setSectionActive(0)}>
                <PlanrButton
                    type="secondary"
                    size="small"
                    className={styles.noSection}
                    onClick={() => pickSection(null)}
                >
                    Без раздела
                </PlanrButton>
            </div>

            <div className={classnames(styles.list, styles.sections)} ref={sectionListRef}>
                {filteredSections.map((section, index) => {
                    const stage = section.stage || NO_STAGE;
                    const previous = index > 0 ? filteredSections[index - 1] : null;
                    const newStage = !previous || (previous.stage || NO_STAGE) !== stage;
                    const position = index + 1;

                    return (
                        <React.Fragment key={section.id}>
                            {newStage && <div className={styles.stage}>{stage}</div>}

                            <div
                                data-index={position}
                                className={classnames(styles.item, styles.sectionItem, {
                                    [styles.active]: position === sectionActive,
                                    [styles.current]: project.id === currentId && section.id === currentSectionId,
                                })}
                                data-tip={section.name}
                                data-tip-note={sectionNote(section, employerId)}
                                onMouseEnter={() => setSectionActive(position)}
                                onClick={() => pickSection(section)}
                            >
                                <span className={styles.code}>{section.code}</span>
                                <span className={styles.itemName}>{section.description}</span>
                                {section.mentorId === employerId && <span className={styles.mark}>отв.</span>}
                                {section.outsourced && <span className={styles.mark}>аутсорс</span>}
                            </div>
                        </React.Fragment>
                    );
                })}

                {!filteredSections.length && <div className={styles.empty}>Раздел не найден</div>}
            </div>
        </div>
    );

    return ReactDOM.createPortal(
        <div
            className={classnames(styles.picker, { [styles.pickerReversed]: sectionsOnLeft })}
            style={position}
            ref={rootRef}
        >
            <div className={classnames(styles.panel, styles.projectsPanel)}>
                <input
                    ref={inputRef}
                    className={classnames(Classes.INPUT, "planr-default-input", styles.search)}
                    placeholder={`Проект (${projects.length})`}
                    autoComplete="off"
                    data-lpignore="true"
                    value={query}
                    onChange={(event) => {
                        setQuery(event.target.value);
                        setActive(0);
                    }}
                    onKeyDown={onKeyDown}
                />

                <div className={styles.list} ref={listRef}>
                    {filtered.map((item, index) => (
                        <div
                            key={item.id}
                            data-index={index}
                            className={classnames(styles.item, {
                                [styles.active]: index === active,
                                [styles.current]: item.id === currentId,
                                [styles.opened]: !!project && item.id === project.id,
                            })}
                            data-tip={item.fullInventoryNumber}
                            data-tip-note={item.name}
                            onMouseEnter={() => setActive(index)}
                            onClick={() => openProject(item, true)}
                        >
                            <span className={styles.color} style={{ backgroundColor: item.color }} />
                            <span className={styles.number}>{item.fullInventoryNumber}</span>
                            <span className={styles.itemName}>{item.name}</span>
                        </div>
                    ))}

                    {!filtered.length && <div className={styles.empty}>Ничего не найдено</div>}
                </div>

                {!!absences.length && (
                    <div className={styles.absences}>
                        {absences.map((absence) => (
                            <button
                                key={absence.type}
                                type="button"
                                title={absence.type}
                                className={classnames(styles.absence, {
                                    [styles.absenceCurrent]: absence.type === currentAbsence,
                                    [styles.absenceWide]: !!absence.wide,
                                })}
                                onClick={() => onPickAbsence(absence)}
                            >
                                {absence.icon}
                                <span className={styles.absenceLabel}>{absence.label}</span>
                            </button>
                        ))}
                    </div>
                )}

                <div className={styles.footer}>
                    <PlanrButton type="graybtn" size="small" onClick={onClear}>
                        Очистить
                    </PlanrButton>
                </div>
            </div>

            {sectionsPanel}
        </div>,
        document.body
    );
};

function scrollToItem(list: HTMLDivElement | null, index: number) {
    const item = list && (list.querySelector(`[data-index="${index}"]`) as HTMLElement | null);

    if (!list || !item) {
        return;
    }

    if (item.offsetTop < list.scrollTop) {
        list.scrollTop = item.offsetTop;
    } else if (item.offsetTop + item.offsetHeight > list.scrollTop + list.clientHeight) {
        list.scrollTop = item.offsetTop + item.offsetHeight - list.clientHeight;
    }
}

/**
 * Куда поставить курсор в разделах (индекс 0 — «Без раздела»): у проекта, открытого
 * на заполненном блоке, — на его раздел; у нового — на единственный раздел, где
 * сотрудник ответственный. Несколько таких разделов — выбирать человеку.
 */
function defaultIndex(
    sections: PlanSection[],
    orderId: string,
    currentId: string | null | undefined,
    currentSectionId: string | null | undefined,
    employerId: string
): number {
    if (orderId === currentId) {
        return currentSectionId ? sections.findIndex((section) => section.id === currentSectionId) + 1 : 0;
    }

    const mentored = sections.filter((section) => !!section.mentorId && section.mentorId === employerId);

    return mentored.length === 1 ? sections.indexOf(mentored[0]) + 1 : 0;
}

/** Вторая строка подсказки раздела: описание и чей он */
function sectionNote(section: PlanSection, employerId: string): string {
    const parts = [
        section.description.trim(),
        section.mentorId === employerId ? "Сотрудник — ответственный за раздел" : "",
        section.outsourced ? "Раздел передан подрядчику" : "",
    ];

    return parts.filter((part) => !!part).join(" — ");
}
